import api, { ApiResponse } from "../api";
import {
  ActiveSession,
  AuthAuditEvent,
  AuthAuditEventsQuery,
  CreateDockEmployeeRequest,
  CreateDockTagRequest,
  CursorPage,
  DockEmployee,
  DockTag,
  EquipmentAssignment,
  EquipmentAssignmentsQuery,
  LastLoginRecord,
  ListEmployeesQuery,
  OperationsSummary,
  PaginatedAuthAuditResponse,
  PaginatedEmployeesResponse,
  PaginatedEquipmentAssignmentsResponse,
  PaginatedScanLogsResponse,
  ReplaceEmployeeTagsRequest,
  ScanLog,
  ScanLogsQuery,
  ScanVolumeBucket,
  UpdateDockEmployeeRequest,
} from "./models";

const BASE = "/scanning";

function getErrorMessage(response: ApiResponse<unknown>): string {
  const err = response.getError?.();
  if (typeof err === "string") return err;
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message: string }).message);
  }
  const data = response.data as { message?: string | string[] } | undefined;
  if (data?.message) {
    return Array.isArray(data.message) ? data.message.join(", ") : data.message;
  }
  return "Request failed";
}

function buildParams(query: Record<string, unknown>): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      params[key] = value.join(",");
    } else {
      params[key] = value as string | number;
    }
  });
  return params;
}

/** Normalize cursor page; tolerate legacy bare-array responses (e.g. GET /employees). */
function normalizeCursorPage<T>(data: unknown): CursorPage<T> {
  if (Array.isArray(data)) {
    return { items: data as T[], nextCursor: null };
  }
  if (data && typeof data === "object") {
    const page = data as { items?: T[]; nextCursor?: string | null };
    return {
      items: Array.isArray(page.items) ? page.items : [],
      nextCursor: page.nextCursor ?? null,
    };
  }
  return { items: [], nextCursor: null };
}

class ScanningService {
  private static instance: ScanningService;

  private constructor() {}

  static getInstance(): ScanningService {
    if (!ScanningService.instance) {
      ScanningService.instance = new ScanningService();
    }
    return ScanningService.instance;
  }

  public listEmployees = async (
    query: ListEmployeesQuery = {}
  ): Promise<PaginatedEmployeesResponse> => {
    const response = await api.get<PaginatedEmployeesResponse | DockEmployee[]>(
      `${BASE}/employees`,
      { params: buildParams(query as Record<string, unknown>) }
    );
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return normalizeCursorPage<DockEmployee>(response.data);
  };

  public getEmployee = async (employeeId: string): Promise<DockEmployee> => {
    const response = await api.get<DockEmployee>(`${BASE}/employees/${employeeId}`);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public createEmployee = async (body: CreateDockEmployeeRequest): Promise<DockEmployee> => {
    const response = await api.post<DockEmployee>(`${BASE}/employees`, body);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public updateEmployee = async (
    employeeId: string,
    body: UpdateDockEmployeeRequest
  ): Promise<DockEmployee> => {
    const response = await api.patch<DockEmployee>(`${BASE}/employees/${employeeId}`, body);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public deactivateEmployee = async (employeeId: string): Promise<DockEmployee> => {
    const response = await api.delete<DockEmployee>(`${BASE}/employees/${employeeId}`);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public replaceEmployeeTags = async (
    employeeId: string,
    body: ReplaceEmployeeTagsRequest
  ): Promise<DockEmployee> => {
    const response = await api.put<DockEmployee>(`${BASE}/employees/${employeeId}/tags`, body);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public listTags = async (terminalNumber: number): Promise<DockTag[]> => {
    const response = await api.get<DockTag[]>(`${BASE}/employees/tags`, {
      params: { terminalNumber },
    });
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public createTag = async (body: CreateDockTagRequest): Promise<DockTag> => {
    const response = await api.post<DockTag>(`${BASE}/employees/tags`, body);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public deleteTag = async (tagId: string): Promise<void> => {
    const response = await api.delete(`${BASE}/employees/tags/${tagId}`);
    if (!response.ok()) throw new Error(getErrorMessage(response));
  };

  public getSummary = async (terminalNumber?: number): Promise<OperationsSummary> => {
    const params: Record<string, number> = {};
    if (terminalNumber != null) params.terminalNumber = terminalNumber;
    const response = await api.get<OperationsSummary>(`${BASE}/operations/summary`, { params });
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public getActiveSessions = async (terminalNumber?: number): Promise<ActiveSession[]> => {
    const params: Record<string, number> = {};
    if (terminalNumber != null) params.terminalNumber = terminalNumber;
    const response = await api.get<ActiveSession[]>(`${BASE}/operations/active-sessions`, { params });
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public getScanVolume = async (terminalNumber?: number): Promise<ScanVolumeBucket[]> => {
    const params: Record<string, number> = {};
    if (terminalNumber != null) params.terminalNumber = terminalNumber;
    const response = await api.get<ScanVolumeBucket[]>(`${BASE}/operations/scan-volume`, { params });
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public getLastLogins = async (terminalNumber?: number, take = 50): Promise<LastLoginRecord[]> => {
    const params: Record<string, number> = { take };
    if (terminalNumber != null) params.terminalNumber = terminalNumber;
    const response = await api.get<LastLoginRecord[]>(`${BASE}/auth-audit/last-logins`, { params });
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public getEmployeeLastLogin = async (employeeId: string): Promise<AuthAuditEvent | null> => {
    const response = await api.get<AuthAuditEvent | null>(
      `${BASE}/auth-audit/employees/${employeeId}/last-login`
    );
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public getAuthEvents = async (query: AuthAuditEventsQuery = {}): Promise<PaginatedAuthAuditResponse> => {
    const response = await api.get<PaginatedAuthAuditResponse | AuthAuditEvent[]>(
      `${BASE}/auth-audit/events`,
      { params: buildParams(query as Record<string, unknown>) }
    );
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return normalizeCursorPage<AuthAuditEvent>(response.data);
  };

  public listScanLogs = async (query: ScanLogsQuery = {}): Promise<PaginatedScanLogsResponse> => {
    const response = await api.get<PaginatedScanLogsResponse | ScanLog[]>(`${BASE}/scan-logs`, {
      params: buildParams(query as Record<string, unknown>),
    });
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return normalizeCursorPage<ScanLog>(response.data);
  };

  public getScanLog = async (scanRequestId: string): Promise<ScanLog> => {
    const response = await api.get<ScanLog>(`${BASE}/scan-logs/${scanRequestId}`);
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };

  public listEquipmentAssignments = async (
    query: EquipmentAssignmentsQuery = {}
  ): Promise<PaginatedEquipmentAssignmentsResponse> => {
    const response = await api.get<PaginatedEquipmentAssignmentsResponse | EquipmentAssignment[]>(
      `${BASE}/equipment-assignments`,
      { params: buildParams(query as Record<string, unknown>) }
    );
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return normalizeCursorPage<EquipmentAssignment>(response.data);
  };

  public getActiveEquipmentAssignments = async (): Promise<EquipmentAssignment[]> => {
    const response = await api.get<EquipmentAssignment[]>(
      `${BASE}/equipment-assignments/office/active`
    );
    if (!response.ok()) throw new Error(getErrorMessage(response));
    return response.data;
  };
}

export default ScanningService;
